import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "../components/Navbar";
import ReelPlayer from "../components/ReelPlayer";
import VideoTrimModal from "../components/VideoTrimModal";
import { api, withRetry } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Copy, Download, Trash2, Sparkles, Share2, Crown, Check, X, Film, Loader2, Pencil, ArrowUp, AlignCenterVertical, ArrowDown, Wand2, Clock, Camera, Image as ImageIcon, ArrowLeft, ArrowRight, Send, QrCode, Smartphone, GripVertical, Mic, Volume2, Layers, PenLine } from "lucide-react";
import { SortableList, SortableItem } from "../components/SortableList";
import { rectSortingStrategy } from "@dnd-kit/sortable";
import CrossCreatePrompt from "../components/CrossCreatePrompt";
import NarrationRecorder from "../components/NarrationRecorder";
import { composeSceneFrameBlob } from "../lib/clientRender";
import { saveBlobToGallery, saveBase64ToGallery } from "../lib/shareUtil";

// Defensive: legacy LLM outputs sometimes baked the hashtag block into
// the caption text. Strip any trailing #-only block so paste-targets
// don't end up with double hashtags.
const stripTrailingHashtags = (text) => {
  if (!text) return "";
  return text.replace(/(\s*#[\p{L}\p{N}_]+)+\s*$/u, "").trimEnd();
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// When axios is configured with responseType: "blob" and the server returns
// a JSON error, err.response.data is a Blob — not a parsed object — so
// `err.response.data.detail` is undefined. This helper extracts the detail
// out of the blob if needed, falling back to a generic message. HTML payloads
// (Cloudflare/nginx error pages) are NEVER shown to the user — we surface a
// friendly fallback that hints at a server-side outage instead.
const extractAxiosErrorDetail = async (err, fallback) => {
  const status = err?.response?.status;
  const direct = err?.response?.data?.detail;
  if (direct && typeof direct === "string") return direct;
  const blob = err?.response?.data;
  if (blob && typeof blob.text === "function") {
    try {
      const txt = (await blob.text()).trim();
      if (txt && (txt[0] === "{" || txt[0] === "[")) {
        try {
          const parsed = JSON.parse(txt);
          if (parsed?.detail && typeof parsed.detail === "string") return parsed.detail;
        } catch (_) {}
      }
      // Looks like an HTML error page (Cloudflare / nginx 5xx) → don't echo it
      if (txt && (txt[0] === "<" || /<!doctype/i.test(txt.slice(0, 30)))) {
        if (status && status >= 500) {
          return "Il server è temporaneamente non raggiungibile. Riprova tra qualche secondo.";
        }
        return fallback;
      }
      // Plain-text non-html short error
      if (txt && txt.length < 200) return txt;
    } catch (_) {}
  }
  if (status === 504 || status === 502 || status === 503) {
    return "Il server non ha risposto in tempo. Riprova tra qualche secondo.";
  }
  if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
    return "Richiesta troppo lenta — il server sta elaborando un video pesante. Riprova.";
  }
  return fallback;
};

const ReelDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  const [narrationRecorderOpen, setNarrationRecorderOpen] = useState(false);
  const [narrationVoice, setNarrationVoice] = useState("voce_uomo_italiana");
  const [narrationMode, setNarrationMode] = useState("overlay");
  const [narrationBusy, setNarrationBusy] = useState(false);
  const [narrationAudioVersion, setNarrationAudioVersion] = useState(0);
  const [narrationCustomScript, setNarrationCustomScript] = useState("");
  const [editingScene, setEditingScene] = useState(null);  // {scene_number, headline, subtitle, text_position, text_size, duration_ms}
  const [savingScene, setSavingScene] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState("");
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  // Cross-format prompt: lets the user spin up a matching carousel from this
  // same idea without re-typing anything in the wizard.
  const [crossOpen, setCrossOpen] = useState(false);
  // When this reel is already paired with a carousel (campaign_id present),
  // resolve the paired carousel id so the banner switches from "Crea
  // carosello" to "Apri carosello".
  const [pairedCarouselId, setPairedCarouselId] = useState(null);
  // "clean"   → download raw AI image (no overlays)
  // "stylized"→ download composed frame WITH headline/CTA/badge baked in
  const [photoStyle, setPhotoStyle] = useState("clean");
  const [socialQrUrl, setSocialQrUrl] = useState("");
  const [socialMode, setSocialMode] = useState("qr"); // "native" | "qr" | "ready"
  // When the user picks the native-share path, we render the MP4 in the
  // background and stash the resulting File here. The Web Share API requires
  // a fresh user gesture on the call to navigator.share(), so we cannot fire
  // it from the same click that started the (~20s) render — instead we show
  // an explicit "Tocca per condividere" button and call share() from THAT
  // button's click handler.
  const [pendingShareFile, setPendingShareFile] = useState(null);
  const [trimFile, setTrimFile] = useState(null);     // raw File pending trim
  const [trimMode, setTrimMode] = useState(null);     // "add" | "replace"
  const [trimUploading, setTrimUploading] = useState(false);
  const navigate = useNavigate();
  const isAgency = user?.plan === "agency";

  useEffect(() => {
    api
      .get(`/reels/${id}`)
      .then((r) => setReel(r.data))
      .catch(() => toast.error("Reel non trovato"))
      .finally(() => setLoading(false));
  }, [id]);

  // Resolve the paired carousel (if any) so the cross-format banner can
  // offer "Apri carosello" rather than suggesting a new generation.
  useEffect(() => {
    const cid = reel?.campaign_id;
    if (!cid) { setPairedCarouselId(null); return; }
    let cancelled = false;
    api.get(`/campaigns/${cid}`)
      .then((r) => { if (!cancelled) setPairedCarouselId(r.data?.carousel_id || null); })
      .catch(() => { if (!cancelled) setPairedCarouselId(null); });
    return () => { cancelled = true; };
  }, [reel?.campaign_id]);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  // Copy caption + hashtags in one shot (mirrors the carousel UX so the
  // user can paste everything into IG/TikTok in a single action).
  const onCopyCaptionWithHashtags = () => {
    if (!reel) return;
    const cleanCaption = stripTrailingHashtags(reel.caption || "");
    const tags = (reel.hashtags || []).slice(0, 5).map((h) => `#${h}`).join(" ");
    const text = cleanCaption + (tags ? `\n\n${tags}` : "");
    navigator.clipboard.writeText(text);
    toast.success("Caption + hashtag copiati!");
  };

  // Download a single scene image (silent download — no share sheet).
  const downloadImage = async (scene, idx) => {
    try {
      const safeBiz = (reel.business_name || "reel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
      await saveBase64ToGallery(
        scene.image_base64,
        `${safeBiz}-scene-${idx + 1}.png`,
        "image/png",
        { title: `Scena ${idx + 1}` /* useShare:false — silent */ },
      );
    } catch (e) {
      toast.error(e?.message || "Errore download immagine");
    }
  };

  // Save every scene as a separate image. Uses plain browser downloads
  // (the share sheet would prompt N times — not what the user wants).
  const downloadAllPhotosOf = async (style) => {
    if (!reel?.scenes?.length) return;
    setPhotoStyle(style);
    setDownloadingPhotos(true);
    const safe = (reel.business_name || "reel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
    const suffix = style === "stylized" ? "con-scritte" : "foto";
    try {
      let saved = 0;
      for (let i = 0; i < reel.scenes.length; i++) {
        const scene = reel.scenes[i];
        let blob = null;
        let ext = "png";
        if (style === "stylized") {
          if (scene.video_id) continue;
          blob = await composeSceneFrameBlob(scene, reel);
          ext = "jpg";
        } else {
          const b64 = scene.image_base64;
          if (!b64) continue;
          const bytes = atob(b64);
          const arr = new Uint8Array(bytes.length);
          for (let k = 0; k < bytes.length; k++) arr[k] = bytes.charCodeAt(k);
          blob = new Blob([arr], { type: "image/png" });
        }
        const ok = await saveBlobToGallery(
          blob,
          `${safe}-scene-${String(i + 1).padStart(2, "0")}-${suffix}.${ext}`,
          { title: `Scena ${i + 1}` /* useShare:false */ },
        );
        if (ok) saved += 1;
        await new Promise((r) => setTimeout(r, 250));
      }
      if (saved > 0) toast.success(`${saved} foto scaricate`);
    } catch (e) {
      toast.error(e?.message || "Errore salvataggio");
    } finally {
      setDownloadingPhotos(false);
    }
  };

  // ---- Voiceover / narration ----
  // We curated this list pragmatically: OpenAI TTS is English-trained,
  // and its native male voices (onyx, ash, sage) drift to English on
  // longer Italian narrations. Workaround: we synthesize male voices
  // by pitch-shifting Nova (the most stable Italian voice) down — this
  // gives an authentic "italiano puro" male timbre.
  const NARRATION_VOICES = [
    // ♂ Synthetic male voices — guaranteed Italian (pitch-shifted Nova)
    { id: "voce_uomo_italiana", label: "Marco", desc: "Maschile · italiano puro ⭐", gender: "m" },
    { id: "voce_uomo_profonda", label: "Lorenzo", desc: "Maschile profondo · italiano puro ⭐", gender: "m" },
    // ♀ Native female voices — stable in Italian
    { id: "nova", label: "Nova", desc: "Femminile · energica", gender: "f" },
    { id: "shimmer", label: "Shimmer", desc: "Femminile · calda", gender: "f" },
    { id: "coral", label: "Coral", desc: "Femminile · espressiva", gender: "f" },
    { id: "alloy", label: "Alloy", desc: "Femminile · neutra", gender: "f" },
  ];

  const generateNarration = async () => {
    if (!reel || narrationBusy) return;
    setNarrationBusy(true);
    try {
      const payload = { voice: narrationVoice, mode: narrationMode };
      if (narrationCustomScript.trim()) payload.custom_script = narrationCustomScript.trim();
      const { data } = await api.post(`/reels/${id}/narration`, payload);
      setReel({
        ...reel,
        narration_voice: data.voice,
        narration_script: data.script,
        narration_has_audio: true,
        narration_mode: data.mode || "overlay",
      });
      // bump the cache-busting key so the <audio> tag re-loads the new file
      setNarrationAudioVersion((v) => v + 1);
      // Show a richer toast with actual duration vs target so the user
      // sees the auto-fit calibration result.
      if (typeof data.duration_s === "number" && typeof data.target_s === "number") {
        const dur = Number(data.duration_s).toFixed(1);
        const tgt = Number(data.target_s).toFixed(1);
        toast.success(`Voce ${data.voice} generata · ${dur}s / ${tgt}s`);
      } else {
        toast.success(`Voce ${data.voice} generata!`);
      }
    } catch (e) {
      // Surface the real backend error so we can debug TTS/Claude/balance
      // issues. Print to console too in case the toast text gets cut off.
      // eslint-disable-next-line no-console
      console.error("Narration generation failed:", e?.response?.status, e?.response?.data, e?.message);
      const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
      const status = e?.response?.status ? ` (HTTP ${e.response.status})` : "";
      toast.error(`Errore generazione voce${status}: ${detail}`, { duration: 8000 });
    } finally {
      setNarrationBusy(false);
    }
  };

  const deleteNarration = async () => {
    if (!reel) return;
    if (!window.confirm("Rimuovere la voce narrante?")) return;
    setNarrationBusy(true);
    try {
      await api.delete(`/reels/${id}/narration`);
      setReel({
        ...reel,
        narration_voice: null,
        narration_script: null,
        narration_has_audio: false,
      });
      setNarrationOpen(false);
      toast.success("Voce narrante rimossa");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione voce");
    } finally {
      setNarrationBusy(false);
    }
  };

  // Build a token-authed URL for the <audio> tag. The endpoint is auth-
  // protected so we need to pass the token (we use the localStorage one
  // that axios reads). For simplicity we fetch via api and convert to
  // blob URL — keeps Auth working on mobile Safari too.
  const [narrationBlobUrl, setNarrationBlobUrl] = useState("");
  useEffect(() => {
    if (!reel?.narration_has_audio) {
      setNarrationBlobUrl("");
      return;
    }
    let revoked = false;
    let createdUrl = "";
    (async () => {
      try {
        // Cache-bust by appending the version number — the same URL
        // serves a different file after each regeneration, so we must
        // force the browser+axios to refetch.
        const res = await api.get(`/reels/${id}/narration/audio?v=${narrationAudioVersion}`, { responseType: "blob" });
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setNarrationBlobUrl(createdUrl);
      } catch (_) {
        setNarrationBlobUrl("");
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [id, reel?.narration_has_audio, narrationAudioVersion]);

  const onDelete = async () => {
    if (!window.confirm("Eliminare questo reel?")) return;
    try {
      await api.delete(`/reels/${id}`);
      toast.success("Reel eliminato");
      navigate("/dashboard");
    } catch {
      toast.error("Errore");
    }
  };

  const onShare = async () => {
    if (!isAgency) {
      setShareOpen(true);
      return;
    }
    setSharing(true);
    try {
      const { data } = await api.post(`/reels/${id}/share`);
      const url = `${window.location.origin}/w/${data.slug}/${data.reel_id}`;
      setShareInfo({ ...data, url });
      setShareOpen(true);
      setReel((r) => r ? { ...r, is_shared: true } : r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore condivisione");
    } finally {
      setSharing(false);
    }
  };

  // Build the caption text the user pastes alongside the reel on Instagram/TikTok/etc.
  const buildSocialCaption = (r) => {
    if (!r) return "";
    const parts = [];
    if (r.title) parts.push(r.title);
    if (r.hook) parts.push(r.hook);
    if (r.caption) parts.push(stripTrailingHashtags(r.caption));
    if (r.cta) parts.push(r.cta);
    if (r.hashtags?.length) parts.push(r.hashtags.slice(0, 5).map((h) => `#${h}`).join(" "));
    return parts.filter(Boolean).join("\n\n");
  };

  // Detect Web Share API + file support (Android Chrome / iOS Safari ≥15)
  const canNativeShareFile = () => {
    if (typeof navigator === "undefined") return false;
    if (!navigator.share || !navigator.canShare) return false;
    try {
      const probe = new File([new Blob(["x"])], "probe.mp4", { type: "video/mp4" });
      return navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  };

  const onOpenSocialShare = async () => {
    setSocialOpen(true);
    setSocialLoading(true);
    setSocialQrUrl("");
    try {
      // 1) Always ask backend for the signed link (used both for QR + as fallback).
      // Auto-retry on transient infrastructure flaps.
      const { data } = await withRetry(
        () => api.post(`/reels/${id}/share-link`),
        { retries: 2, onRetry: (n) => toast.message(`Connessione instabile, riprovo (${n}/3)...`) },
      );
      const absUrl = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      setSocialQrUrl(absUrl);

      // 2) On mobile with Web Share API + file support → render then prompt
      // user for a fresh gesture-driven share click.
      if (canNativeShareFile()) {
        setSocialMode("native");
        try {
          // Render MP4 client-side (~15-30s). We CANNOT call navigator.share()
          // after this completes because the user gesture from the original
          // click is no longer "transient" — Chrome/Safari throw
          //   "Failed to execute 'share' on 'Navigator': Must be handling
          //    a user gesture to perform a share request."
          // Instead, stash the File and present a "Tap to share" button that
          // invokes navigator.share() from a fresh click handler.
          const blob = await renderMp4Async();
          const safe = (reel?.business_name || "socialreel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
          const file = new File([blob], `${safe}-reel.mp4`, { type: "video/mp4" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            setPendingShareFile(file);
            setSocialMode("ready");
            return;
          }
        } catch (err) {
          if (err?.name === "AbortError") {
            setSocialOpen(false);
            return;
          }
          const msg = await extractAxiosErrorDetail(err, err?.message || null);
          if (msg) {
            toast.error(msg);
            setSocialOpen(false);
            return;
          }
        }
        setSocialMode("qr");
      } else {
        setSocialMode("qr");
      }
    } catch (err) {
      const msg = await extractAxiosErrorDetail(err, "Errore generazione link condivisione");
      toast.error(msg);
      setSocialOpen(false);
    } finally {
      setSocialLoading(false);
    }
  };

  const onCopySocialCaption = () => {
    const txt = buildSocialCaption(reel);
    navigator.clipboard.writeText(txt);
    toast.success("Caption copiata! Incollala dopo aver caricato il video");
  };

  // Invoked from a FRESH click handler on the "Tocca per condividere" button.
  // CRITICAL: this MUST be a synchronous function (no async/await) because
  // some Android Chrome builds drop the "transient activation" flag if
  // navigator.share() is reached through the async microtask queue. We call
  // share() synchronously and chain .then/.catch on the returned Promise.
  const onShareNow = () => {
    if (!pendingShareFile) return;
    const text = buildSocialCaption(reel);
    let sharePromise;
    try {
      sharePromise = navigator.share({
        files: [pendingShareFile],
        title: reel?.title || "Il mio reel",
        text,
      });
    } catch (err) {
      // Synchronous throw (e.g. NotAllowedError on iOS Safari when not in a
      // user gesture). Surface to the user.
      toast.error(err?.message || "Condivisione fallita");
      return;
    }
    Promise.resolve(sharePromise)
      .then(() => {
        toast.success("Condiviso!");
        setSocialOpen(false);
        setPendingShareFile(null);
        setSocialMode("qr");
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          toast.error(err?.message || "Condivisione fallita");
        }
      });
  };

  // Reset transient state when the modal is closed by the user.
  const closeSocialModal = () => {
    if (socialLoading) return;
    setSocialOpen(false);
    setPendingShareFile(null);
    setSocialMode("qr");
  };

  const onUnshare = async () => {
    try {
      await api.post(`/reels/${id}/unshare`);
      setReel((r) => r ? { ...r, is_shared: false } : r);
      setShareInfo(null);
      setShareOpen(false);
      toast.success("Condivisione disattivata");
    } catch {
      toast.error("Errore");
    }
  };

  // Render the reel ENTIRELY in the user's browser using ffmpeg.wasm.
  // Why: the backend FFmpeg path occasionally OOM-kills on smaller deploy
  // pods when a reel mixes user-uploaded videos with AI images. Doing this
  // client-side removes the infrastructure dependency — the only failure
  // modes are now the user's browser/device, which we can detect.
  const renderMp4Async = async ({ onProgress } = {}) => {
    const { renderReelClient } = await import("../lib/clientRender");
    if (!reel) throw new Error("Reel non caricato");
    return await renderReelClient(reel, {
      onProgress: (pct) => {
        if (onProgress) onProgress(pct);
      },
    });
  };

  const onExportMp4 = async () => {
    setExporting(true);
    const tid = toast.loading("Preparazione MP4... 0%");
    try {
      const blob = await renderMp4Async({
        onProgress: (pct) => toast.loading(`Elaborazione MP4... ${pct}%`, { id: tid }),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (reel?.business_name || "socialreel").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
      a.download = `${safe}-reel.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("MP4 scaricato!", { id: tid });
    } catch (err) {
      const msg = await extractAxiosErrorDetail(err, err?.message || "Errore export MP4");
      toast.error(msg, { id: tid });
    } finally {
      setExporting(false);
    }
  };

  const onSaveScene = async () => {
    if (!editingScene) return;
    setSavingScene(true);
    try {
      const body = {
        headline: editingScene.headline,
        subtitle: editingScene.subtitle,
        text_position: editingScene.text_position,
        text_size: editingScene.text_size,
        duration_ms: editingScene.duration_ms,
      };
      if (editingScene.new_image_base64) {
        body.image_base64 = editingScene.new_image_base64;
      }
      const { data } = await api.patch(`/reels/${id}/scenes/${editingScene.scene_number}`, body);
      setReel(data);
      toast.success("Scena aggiornata! Riscarica l'MP4 per vederla.");
      setEditingScene(null);
      setRegenPrompt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingScene(false);
    }
  };

  const [regenMode, setRegenMode] = useState("edit"); // "edit" | "fresh"

  const onRegenerateImage = async () => {
    if (!editingScene) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(
        `/reels/${id}/scenes/${editingScene.scene_number}/regenerate-image`,
        {
          prompt_override: regenPrompt.trim() || undefined,
          edit_mode: regenMode === "edit",
          // CRITICAL: if the user has just uploaded a replacement photo
          // in this editor session (but hasn't saved the scene yet), the
          // backend must use THAT image as the reference for the AI edit
          // — not the stale image still stored in the DB.
          override_image_base64: editingScene.new_image_base64 || undefined,
        },
        { timeout: 120000 },
      );
      setReel(data);
      const idx0 = editingScene.scene_number - 1;
      const newB64 = data.scenes[idx0]?.image_base64;
      setEditingScene((cur) => cur ? { ...cur, new_image_base64: null } : cur);
      toast.success(regenMode === "edit" ? "Foto modificata dall'AI!" : "Nuova foto generata dall'AI!");
      void newB64;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rigenerazione AI");
    } finally {
      setRegenerating(false);
    }
  };

  const onDeleteScene = async (sceneNumber) => {
    if (!window.confirm(`Eliminare la scena ${sceneNumber}?`)) return;
    try {
      const { data } = await api.delete(`/reels/${id}/scenes/${sceneNumber}`);
      setReel(data);
      toast.success("Scena eliminata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const onMoveScene = async (sceneNumber, direction) => {
    if (!reel?.scenes?.length) return;
    const total = reel.scenes.length;
    const idx0 = sceneNumber - 1;
    const target = direction === "left" ? idx0 - 1 : idx0 + 1;
    if (target < 0 || target >= total) return;
    const order = reel.scenes.map((s) => s.scene_number);
    [order[idx0], order[target]] = [order[target], order[idx0]];
    try {
      const { data } = await api.put(`/reels/${id}/scenes/reorder`, { order });
      setReel(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore riordino");
    }
  };

  // Drag & drop reorder for scenes. `newIds` are the current scene_numbers
  // in the desired order. Optimistic update so the grid rearranges instantly,
  // then sync with the backend (which renumbers 1..N).
  const onDragReorderScenes = async (newIds) => {
    if (!reel?.scenes?.length) return;
    const previous = reel;
    const bySceneNumber = new Map(reel.scenes.map((s) => [s.scene_number, s]));
    const reorderedScenes = newIds.map((sn, i) => ({
      ...bySceneNumber.get(sn),
      scene_number: i + 1,
    }));
    setReel({ ...reel, scenes: reorderedScenes });
    try {
      const { data } = await api.put(`/reels/${id}/scenes/reorder`, { order: newIds });
      setReel(data);
      toast.success("Ordine aggiornato");
    } catch (err) {
      setReel(previous);
      toast.error(err?.response?.data?.detail || "Errore riordino");
    }
  };

  // Upload a user-picked video file to the backend; returns {video_id, duration_ms}.
  // `trim` is optional: { start_ms, end_ms, crop: {x,y,w,h}|null } — when omitted,
  // the source must already be ≤ 20s and is uploaded as-is. Auto-retries up to
  // 2 times on transient 5xx (Cloudflare) errors. `onProgress(pct)` is called
  // with 0-100 during the upload phase.
  const uploadVideoFile = async (file, trim, onProgress) => {
    if (!file) throw new Error("Nessun file");
    if (file.size > 120 * 1024 * 1024) throw new Error("Video troppo grande (max 120MB)");
    return withRetry(
      async () => {
        const fd = new FormData();
        fd.append("file", file);
        if (trim) {
          fd.append("start_ms", String(Math.round(trim.start_ms)));
          fd.append("end_ms", String(Math.round(trim.end_ms)));
          if (trim.crop) {
            fd.append("crop_x", String(Math.round(trim.crop.x)));
            fd.append("crop_y", String(Math.round(trim.crop.y)));
            fd.append("crop_w", String(Math.round(trim.crop.w)));
            fd.append("crop_h", String(Math.round(trim.crop.h)));
          }
        }
        const { data } = await api.post(`/reels/uploads/video`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000,
          onUploadProgress: (e) => {
            if (onProgress && e.total) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          },
        });
        return data; // {video_id, duration_ms, size}
      },
      {
        retries: 2,
        onRetry: (n) => toast.message(`Connessione instabile, riprovo (${n}/3)...`),
      },
    );
  };

  // Read a video file's duration + dimensions client-side (via <video> metadata)
  // so we can decide whether to skip the trim modal.
  const probeVideoMeta = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch (_) {} };
    v.onloadedmetadata = () => {
      const meta = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      cleanup();
      resolve(meta);
    };
    v.onerror = () => { cleanup(); reject(new Error("Video non leggibile")); };
    v.src = url;
  });

  const TARGET_VIDEO_AR = 9 / 16;

  // Decide whether the video needs the trim/crop modal.
  // Returns a "trim" payload (or null if no edit needed).
  const planVideoEditing = async (file) => {
    let meta;
    try {
      meta = await probeVideoMeta(file);
    } catch (e) {
      // Can't read metadata in browser — let the server decide
      return { needsModal: false, trim: null };
    }
    const ar = meta.width && meta.height ? meta.width / meta.height : TARGET_VIDEO_AR;
    const off916 = Math.abs(ar - TARGET_VIDEO_AR) > 0.005;
    const tooLong = (meta.duration || 0) > 20.05;
    if (tooLong || off916) {
      return { needsModal: true, trim: null };
    }
    return { needsModal: false, trim: null };
  };

  const onAddScenePhoto = async (file, label) => {
    if (!file) return;
    try {
      const b64 = await compressImageFile(file);
      const { data } = await api.post(`/reels/${id}/scenes`, {
        image_base64: b64,
        headline: label || "NUOVA SCENA",
        subtitle: "",
        text_position: "auto",
        text_size: "normal",
      });
      setReel(data);
      toast.success("Scena aggiunta! Apri la matita per personalizzare il testo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore aggiunta");
    }
  };

  const finalizeAddSceneVideo = async (file, trim) => {
    const tid = toast.loading("Caricamento video... 0%");
    try {
      const up = await uploadVideoFile(file, trim, (pct) => {
        toast.loading(`Caricamento video... ${pct}%`, { id: tid });
      });
      toast.loading("Elaborazione...", { id: tid });
      const { data } = await api.post(`/reels/${id}/scenes`, {
        video_id: up.video_id,
        headline: "NUOVA SCENA",
        subtitle: "",
        text_position: "auto",
        text_size: "normal",
      });
      setReel(data);
      toast.success("Video aggiunto come scena!", { id: tid });
    } catch (err) {
      const msg = await extractAxiosErrorDetail(err, "Errore caricamento video");
      toast.error(msg, { id: tid });
      throw err;
    }
  };

  const finalizeReplaceSceneVideo = async (file, trim) => {
    if (!editingScene) return;
    const tid = toast.loading("Caricamento video... 0%");
    try {
      const up = await uploadVideoFile(file, trim, (pct) => {
        toast.loading(`Caricamento video... ${pct}%`, { id: tid });
      });
      toast.loading("Elaborazione...", { id: tid });
      const { data } = await api.patch(`/reels/${id}/scenes/${editingScene.scene_number}`, {
        video_id: up.video_id,
      });
      setReel(data);
      setEditingScene((cur) =>
        cur
          ? {
              ...cur,
              is_video: true,
              new_image_base64: null,
              duration_ms: Math.min(30000, Math.max(1000, up.duration_ms || cur.duration_ms || 5000)),
            }
          : cur,
      );
      toast.success("Video applicato! Ricordati di salvare il testo.", { id: tid });
    } catch (err) {
      const msg = await extractAxiosErrorDetail(err, "Errore caricamento video");
      toast.error(msg, { id: tid });
      throw err;
    }
  };

  const onAddSceneVideo = async (file) => {
    if (!file) return;
    const plan = await planVideoEditing(file);
    if (plan.needsModal) {
      setTrimFile(file);
      setTrimMode("add");
      return;
    }
    try { await finalizeAddSceneVideo(file, null); } catch (_) {}
  };

  const onReplaceSceneVideo = async (file) => {
    if (!editingScene || !file) return;
    const plan = await planVideoEditing(file);
    if (plan.needsModal) {
      setTrimFile(file);
      setTrimMode("replace");
      return;
    }
    setSavingScene(true);
    try { await finalizeReplaceSceneVideo(file, null); } finally { setSavingScene(false); }
  };

  const onTrimConfirm = async (trim) => {
    if (!trimFile || !trimMode) return;
    setTrimUploading(true);
    try {
      if (trimMode === "add") {
        await finalizeAddSceneVideo(trimFile, trim);
      } else {
        await finalizeReplaceSceneVideo(trimFile, trim);
      }
      setTrimFile(null);
      setTrimMode(null);
    } catch (_) {
      // error already toasted; keep modal open so user can retry/cancel
    } finally {
      setTrimUploading(false);
    }
  };

  const onTrimCancel = () => {
    if (trimUploading) return;
    setTrimFile(null);
    setTrimMode(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 flex items-center gap-3 text-zinc-500">
          <span className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot" />
          <span className="font-mono text-xs tracking-widest uppercase">Loading reel...</span>
        </div>
      </div>
    );
  }

  if (!reel) return null;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <Link
          to="/dashboard"
          data-testid="detail-back"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="font-mono text-xs uppercase tracking-widest">Dashboard</span>
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex justify-center items-start lg:sticky lg:top-24 self-start">
            <ReelPlayer reel={reel} />
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
              · {reel.category}
            </div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mb-4" data-testid="detail-title">
              {reel.title}
            </h1>
            <p className="text-zinc-400 font-body mb-10 text-lg">
              <span className="text-[#FF4400]">Hook:</span> {reel.hook}
            </p>

            {/* Cross-create banner: propose a matching carousel.
                When this reel is already paired with a carousel
                (campaign_id resolved), flip the banner to "Apri carosello". */}
            <div className="mb-8 rounded-lg border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-[#FF4400]/5 to-transparent p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 shrink-0 rounded-md bg-gradient-to-br from-violet-500 to-[#FF4400] flex items-center justify-center shadow-sm">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-300">· Idea ↔ Carosello</div>
                  <p className="text-sm text-white font-body truncate">
                    {pairedCarouselId ? (
                      <>Hai già un <strong>carosello</strong> collegato a questa idea.</>
                    ) : (
                      <>Vuoi creare anche un <strong>carosello</strong> con la stessa idea?</>
                    )}
                  </p>
                </div>
              </div>
              {pairedCarouselId ? (
                <button
                  onClick={() => navigate(`/carousel/${pairedCarouselId}`)}
                  data-testid="reel-open-paired-carousel"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white font-display font-bold text-sm rounded-md transition-colors shrink-0"
                >
                  <Layers className="w-4 h-4" />
                  Apri carosello
                </button>
              ) : (
                <button
                  onClick={() => setCrossOpen(true)}
                  data-testid="reel-cross-create-button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white font-display font-bold text-sm rounded-md transition-colors shrink-0"
                >
                  <Sparkles className="w-4 h-4" />
                  Crea carosello
                </button>
              )}
            </div>

            <div className="space-y-5">
              <Section
                label="Call To Action"
                onCopy={() => copyText(reel.cta, "CTA")}
              >
                <div className="text-white font-display font-bold">{reel.cta}</div>
                {/* Show/hide CTA toggle — applies to MP4 export and
                    the in-app preview. Default ON (legacy reels with
                    no `show_cta` field keep the CTA visible). */}
                <label
                  className="mt-3 flex items-center gap-3 cursor-pointer"
                  data-testid="reel-cta-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={reel.show_cta !== false}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      // Optimistic update so the preview reacts instantly.
                      setReel((r) => (r ? { ...r, show_cta: next } : r));
                      try {
                        await api.patch(`/reels/${id}`, { show_cta: next });
                      } catch (err) {
                        toast.error("Errore aggiornamento CTA");
                        setReel((r) => (r ? { ...r, show_cta: !next } : r));
                      }
                    }}
                    className="w-4 h-4 accent-[#FF4400]"
                    data-testid="reel-cta-toggle"
                  />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                    Mostra CTA nell&apos;export MP4
                  </span>
                </label>
              </Section>

              {/* One-shot copy: caption + hashtag together, ready to paste
                  into Instagram / TikTok. Mirrors the carousel UX. */}
              <button
                onClick={onCopyCaptionWithHashtags}
                data-testid="reel-copy-caption-hashtags"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copia caption + hashtag
              </button>

              <Section
                label="Caption"
                onCopy={() => copyText(stripTrailingHashtags(reel.caption), "Caption")}
              >
                <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-body" data-testid="detail-caption">
                  {stripTrailingHashtags(reel.caption)}
                </pre>
              </Section>

              <Section
                label="Hashtag"
                onCopy={() => copyText((reel.hashtags || []).slice(0, 5).map((h) => `#${h}`).join(" "), "Hashtag")}
              >
                <div className="flex flex-wrap gap-1.5">
                  {(reel.hashtags || []).slice(0, 5).map((h) => (
                    <span
                      key={h}
                      className="px-2 py-1 text-xs font-mono text-[#FF4400] bg-[#FF4400]/10 border border-[#FF4400]/30 rounded-sm"
                    >
                      #{h}
                    </span>
                  ))}
                </div>
              </Section>

              <Section label="Musica suggerita">
                <div className="text-zinc-300 font-body">{reel.music_mood}</div>
              </Section>

              {/* Voiceover / narration panel — generate or remove a TTS
                  voice that plays over the reel. The client MP4 exporter
                  reads `reel.narration_has_audio` and mixes the audio in. */}
              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-5" data-testid="narration-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-[#FF4400]" />
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                      · Voce narrante
                    </div>
                  </div>
                  {reel.narration_has_audio && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                      {reel.narration_voice}
                    </span>
                  )}
                </div>

                {reel.narration_has_audio ? (
                  // -- Existing narration: preview + delete + regen ---
                  <div className="space-y-3">
                    <p className="text-sm text-zinc-400 font-body">
                      Il reel ha già una voce narrante (<span className="text-white font-bold">{reel.narration_voice}</span>).{" "}
                      {reel.narration_mode === "respect_videos"
                        ? "La voce si fermerà sulle scene video lasciando l'audio originale."
                        : "Verrà mixata sopra tutto il reel."}
                    </p>
                    {narrationBlobUrl && (
                      <audio
                        controls
                        src={narrationBlobUrl}
                        className="w-full"
                        data-testid="narration-audio-player"
                      />
                    )}
                    {reel.narration_script && (
                      <details className="text-sm text-zinc-400">
                        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                          Mostra script
                        </summary>
                        <div className="mt-2 p-3 bg-zinc-900 border border-zinc-800 rounded-sm whitespace-pre-wrap font-body">
                          {reel.narration_script}
                        </div>
                      </details>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Pre-fill state from the existing reel so the
                          // user sees their current settings before editing.
                          // If the saved voice was removed from the picker
                          // (e.g. legacy onyx/ash/sage that drift to English),
                          // fall back to the new synthetic male voice.
                          const saved = reel.narration_voice || "nova";
                          const allowed = NARRATION_VOICES.map((v) => v.id);
                          const maleLegacy = ["onyx", "ash", "echo", "fable", "sage"];
                          const fallback = maleLegacy.includes(saved) ? "voce_uomo_italiana" : "nova";
                          setNarrationVoice(allowed.includes(saved) ? saved : fallback);
                          setNarrationMode(reel.narration_mode || "overlay");
                          setNarrationOpen((v) => !v);
                        }}
                        data-testid="narration-regen-toggle"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-[#FF4400] hover:text-white font-display font-bold text-xs rounded-sm transition-colors"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Rigenera
                      </button>
                      <button
                        onClick={() => setNarrationRecorderOpen(true)}
                        data-testid="narration-rerecord-mine"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-500/50 hover:border-red-500 text-red-300 hover:text-white font-display font-bold text-xs rounded-sm transition-colors"
                      >
                        <Mic className="w-3.5 h-3.5" />
                        Rifai con la mia voce
                      </button>
                      <button
                        onClick={deleteNarration}
                        disabled={narrationBusy}
                        data-testid="narration-delete"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-zinc-800 text-zinc-500 hover:border-red-500/50 hover:text-red-400 disabled:opacity-40 font-display font-bold text-xs rounded-sm transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  // -- No narration yet: show the call-to-action button ---
                  <>
                    <p className="text-sm text-zinc-400 font-body mb-4">
                      Aggiungi una voce narrante AI al tuo reel. Lo script verrà generato automaticamente dai testi del reel, e potrai scegliere fra 6 voci professionali.
                    </p>
                    <p className="text-xs text-zinc-500 font-body mb-4 italic">
                      Le voci con ⭐ sono ottimizzate per l'italiano puro. Per i reel più lunghi, consigliamo queste o le voci femminili Nova/Shimmer.
                    </p>
                    {!narrationOpen && (
                      <div className="space-y-2">
                        <button
                          onClick={() => setNarrationOpen(true)}
                          data-testid="narration-open"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                        >
                          <Volume2 className="w-4 h-4" />
                          Genera voce AI
                        </button>
                        <button
                          onClick={() => setNarrationRecorderOpen(true)}
                          data-testid="narration-record-mine-top"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-red-500/60 hover:border-red-500 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-white font-display font-bold rounded-sm transition-colors"
                        >
                          <Mic className="w-4 h-4" />
                          Registra la mia voce
                        </button>
                      </div>
                    )}
                  </>
                )}

                {narrationOpen && (
                  // -- Voice picker + generate ---
                  <div className="mt-4 pt-4 border-t border-zinc-800 space-y-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                        Scegli la voce
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {NARRATION_VOICES.map((v) => {
                          const sel = narrationVoice === v.id;
                          return (
                            <button
                              key={v.id}
                              onClick={() => setNarrationVoice(v.id)}
                              data-testid={`narration-voice-${v.id}`}
                              className={`text-left p-3 border rounded-sm transition-all ${
                                sel
                                  ? "border-[#FF4400] bg-[#FF4400]/10"
                                  : "border-zinc-800 hover:border-zinc-600"
                              }`}
                            >
                              <div className={`font-display font-bold text-sm ${sel ? "text-white" : "text-zinc-300"}`}>
                                {v.gender === "f" ? "♀" : "♂"} {v.label}
                              </div>
                              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                                {v.desc}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <details className="text-sm">
                      <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                        Personalizza lo script (opzionale)
                      </summary>
                      <textarea
                        rows={4}
                        value={narrationCustomScript}
                        onChange={(e) => setNarrationCustomScript(e.target.value)}
                        placeholder="Lascia vuoto per usare lo script generato automaticamente dall'AI dai tuoi testi"
                        className="mt-2 w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-sm rounded-sm font-body focus:outline-none focus:ring-1 focus:ring-[#FF4400] resize-none"
                        data-testid="narration-custom-script"
                      />
                    </details>

                    {/* Voice-over behavior on user-uploaded video scenes.
                        We render both options with a tiny ASCII-style
                        timeline so the user understands the exact behavior
                        before generating (this was a recurring confusion
                        point — users expected the voice to cover the FULL
                        reel even in respect_videos mode). */}
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                        Sui video del reel
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          {
                            id: "overlay",
                            label: "Voce sempre attiva (consigliata)",
                            desc: "La voce copre TUTTO il reel, anche sopra ai video. Audio originale del video al 25%.",
                            timeline: "🎙️━━━━━━━━━━━━━━━━━ (voce ovunque)",
                          },
                          {
                            id: "respect_videos",
                            label: "Pausa sui video",
                            desc: "Sulle scene VIDEO la voce si ferma e si sente solo l'audio originale. La voce copre solo le slide immagini.",
                            timeline: "🎬━━━━ 🎙️━━━━━━━━━━ (voce solo sulle immagini)",
                          },
                        ].map((opt) => {
                          const sel = narrationMode === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => setNarrationMode(opt.id)}
                              data-testid={`narration-mode-${opt.id}`}
                              className={`text-left p-3 border rounded-sm transition-all ${
                                sel
                                  ? "border-[#FF4400] bg-[#FF4400]/10"
                                  : "border-zinc-800 hover:border-zinc-600"
                              }`}
                            >
                              <div className={`font-display font-bold text-sm ${sel ? "text-white" : "text-zinc-300"}`}>
                                {opt.label}
                              </div>
                              <div className="font-body text-[11px] text-zinc-500 mt-1 leading-snug">
                                {opt.desc}
                              </div>
                              <div className="font-mono text-[10px] text-zinc-600 mt-2 tracking-tight">
                                {opt.timeline}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => { setNarrationOpen(false); setNarrationCustomScript(""); }}
                        className="px-4 py-2 text-xs text-zinc-400 hover:text-white font-mono uppercase tracking-widest"
                      >
                        Annulla
                      </button>
                      <button
                        type="button"
                        onClick={() => { setNarrationOpen(false); setNarrationRecorderOpen(true); }}
                        data-testid="narration-record-mine"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-500/60 hover:border-red-500 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-white font-display font-bold text-sm rounded-sm transition-colors"
                      >
                        <Mic className="w-4 h-4" />
                        Registra la mia voce
                      </button>
                      <button
                        onClick={generateNarration}
                        disabled={narrationBusy}
                        data-testid="narration-generate"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold text-sm rounded-sm transition-colors"
                      >
                        {narrationBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {narrationBusy ? "Generazione voce..." : "Genera voce"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* How to publish */}
              <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Film className="w-4 h-4 text-[#FF4400]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                    Come pubblicare
                  </div>
                </div>
                <ol className="space-y-2 text-sm text-zinc-300 font-body">
                  <li className="flex gap-3">
                    <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">1</span>
                    <span>Clicca <span className="text-white font-bold">Scarica MP4</span> (qui sotto) per ottenere il video pronto 1080×1920</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">2</span>
                    <span>Copia la <span className="text-white font-bold">Caption</span> e gli <span className="text-white font-bold">Hashtag</span> dai box sopra</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">3</span>
                    <span>Apri Instagram/TikTok/YouTube Shorts, crea un nuovo reel, carica l'MP4 e incolla caption + hashtag</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">4</span>
                    <span>Scegli una musica dal loro catalogo (consiglio: <span className="text-white font-mono text-xs">{reel.music_mood}</span>) e pubblica</span>
                  </li>
                </ol>
              </div>

              {/* Scenes grid — drag scenes to reorder */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Scene generate ({reel.scenes.length})
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                    · Trascina per riordinare
                  </div>
                </div>
                <SortableList
                  items={reel.scenes.map((s) => s.scene_number)}
                  strategy={rectSortingStrategy}
                  onReorder={onDragReorderScenes}
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {reel.scenes.map((scene, i) => (
                      <SortableItem
                        key={scene.scene_number}
                        id={scene.scene_number}
                        testId={`scene-card-${i}`}
                        className="relative group border border-zinc-800 bg-zinc-950 rounded-sm overflow-hidden cursor-grab active:cursor-grabbing"
                      >
                      <div className="aspect-[9/16] relative">
                        <img
                          src={`data:image/png;base64,${scene.image_base64}`}
                          alt={`Scene ${i + 1}`}
                          className="w-full h-full object-cover pointer-events-none"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" />

                        {/* Drag affordance — purely visual; the whole card
                            is the drag handle (PointerSensor uses 6px
                            threshold so clicks still pass through). */}
                        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur rounded-sm p-1 text-white/80 pointer-events-none">
                          <GripVertical className="w-3.5 h-3.5" />
                        </div>

                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => { setEditingScene({
                              scene_number: scene.scene_number,
                              headline: scene.headline,
                              subtitle: scene.subtitle,
                              text_position: scene.text_position || "center",
                              text_size: scene.text_size || "normal",
                              duration_ms: scene.duration_ms || 5000,
                              is_video: !!scene.video_id,
                            }); setRegenPrompt(""); }}
                            data-testid={`scene-edit-${i}`}
                            className="w-8 h-8 rounded-sm bg-black/60 backdrop-blur border border-white/10 text-white hover:bg-[#FF4400] hover:border-[#FF4400] transition-all flex items-center justify-center"
                            title="Modifica scena"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => downloadImage(scene, i)}
                            data-testid={`scene-download-${i}`}
                            className="w-8 h-8 rounded-sm bg-black/60 backdrop-blur border border-white/10 text-white hover:bg-[#FF4400] hover:border-[#FF4400] transition-all flex items-center justify-center"
                            title={photoStyle === "stylized" ? "Scarica con scritte" : "Scarica solo immagine"}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          {reel.scenes.length > 2 && (
                            <button
                              onClick={() => onDeleteScene(scene.scene_number)}
                              data-testid={`scene-delete-${i}`}
                              className="w-8 h-8 rounded-sm bg-black/60 backdrop-blur border border-white/10 text-white hover:bg-red-500 hover:border-red-500 transition-all flex items-center justify-center"
                              title="Elimina scena"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        <div className="absolute bottom-2 left-2 right-2">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF4400]">
                              Scene {i + 1} · {scene.text_position || "center"}
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => onMoveScene(scene.scene_number, "left")}
                                disabled={i === 0}
                                data-testid={`scene-move-left-${i}`}
                                className="w-7 h-7 rounded-sm bg-black/70 backdrop-blur border border-white/15 text-white hover:bg-[#FF4400] hover:border-[#FF4400] transition-all flex items-center justify-center disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-black/70 disabled:hover:border-white/15"
                                title="Sposta indietro"
                              >
                                <ArrowLeft className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => onMoveScene(scene.scene_number, "right")}
                                disabled={i === reel.scenes.length - 1}
                                data-testid={`scene-move-right-${i}`}
                                className="w-7 h-7 rounded-sm bg-black/70 backdrop-blur border border-white/15 text-white hover:bg-[#FF4400] hover:border-[#FF4400] transition-all flex items-center justify-center disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-black/70 disabled:hover:border-white/15"
                                title="Sposta avanti"
                              >
                                <ArrowRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="font-display font-bold text-white text-xs leading-tight line-clamp-2">
                            {scene.headline}
                          </div>
                        </div>
                      </div>
                      </SortableItem>
                    ))}
                  </div>
                </SortableList>
                {reel.scenes.length < 15 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    <AddSceneMediaCard
                      onPhoto={(f) => {
                        const label = (f.name.replace(/\.[^.]+$/, "").slice(0, 24) || "Nuova scena").toUpperCase();
                        return onAddScenePhoto(f, label);
                      }}
                      onVideo={(f) => onAddSceneVideo(f)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Soft warnings for long videos / over-Instagram-cap reels.
                Shown above the action row so the user sees them before
                clicking "Scarica MP4". Computed inline from scene state. */}
            {(() => {
              const totalMs = (reel.scenes || []).reduce((s, sc) => s + (sc.duration_ms || 5000), 0);
              const longVideoCount = (reel.scenes || []).filter(
                (sc) => sc.video_id && (sc.duration_ms || 0) > 20000
              ).length;
              const overInsta = totalMs > 90000;
              const oomRisk = longVideoCount >= 3;
              if (!overInsta && !oomRisk) return null;
              return (
                <div className="mt-8 space-y-2" data-testid="reel-export-warnings">
                  {overInsta && (
                    <div
                      className="flex items-start gap-2 px-3 py-2 border border-orange-500/40 bg-orange-500/10 rounded-sm"
                      data-testid="reel-warning-instagram-cap"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 shrink-0" />
                      <div className="text-[11px] text-orange-200 font-body leading-relaxed">
                        <strong>Durata totale {(totalMs / 1000).toFixed(0)}s</strong> — oltre il limite Instagram Reels (90s).
                        Va bene per <strong>TikTok</strong> (max 10min) e <strong>YouTube Shorts</strong> (max 60s ora, esteso a 3min in alcune aree).
                      </div>
                    </div>
                  )}
                  {oomRisk && (
                    <div
                      className="flex items-start gap-2 px-3 py-2 border border-red-500/40 bg-red-500/10 rounded-sm"
                      data-testid="reel-warning-oom-risk"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                      <div className="text-[11px] text-red-200 font-body leading-relaxed">
                        <strong>{longVideoCount} video lunghi (&gt;20s)</strong> — su mobile l&apos;export MP4 può
                        fallire per esaurimento memoria. Consigliato: esegui da <strong>desktop</strong> o accorcia qualche video.
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex gap-3 mt-10 pt-8 border-t border-zinc-800 flex-wrap">
              <button
                onClick={onOpenSocialShare}
                data-testid="detail-share-social"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                Condividi sui social
              </button>
              <button
                onClick={onExportMp4}
                disabled={exporting}
                data-testid="detail-export-mp4"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#FF4400] text-[#FF4400] hover:bg-[#FF4400] hover:text-white disabled:opacity-50 font-display font-bold rounded-sm transition-colors"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                {exporting ? "Rendering MP4..." : "Scarica MP4"}
              </button>
              <button
                onClick={() => downloadAllPhotosOf("clean")}
                disabled={downloadingPhotos}
                data-testid="detail-download-photos"
                className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 text-white hover:border-[#FF4400] hover:text-[#FF4400] disabled:opacity-50 font-display font-bold rounded-sm transition-colors"
                title="Solo immagini AI, senza titoli o CTA"
              >
                {downloadingPhotos && photoStyle === "clean" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                Foto · Senza scritte
              </button>
              <button
                onClick={() => downloadAllPhotosOf("stylized")}
                disabled={downloadingPhotos}
                data-testid="detail-download-photos-stylized"
                className="inline-flex items-center gap-2 px-5 py-3 border border-violet-500 text-violet-300 hover:bg-violet-600 hover:text-white disabled:opacity-50 font-display font-bold rounded-sm transition-colors"
                title="Foto con badge brand, titolo grande e CTA — pronte da postare"
              >
                {downloadingPhotos && photoStyle === "stylized" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                Foto · Con scritte
              </button>
              <button
                onClick={() => navigate("/create")}
                data-testid="detail-new-reel"
                className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-white font-body font-medium rounded-sm transition-all"
              >
                <Sparkles className="w-4 h-4" />
                Nuovo reel
              </button>
              <button
                onClick={onShare}
                disabled={sharing}
                data-testid="detail-share"
                className={`inline-flex items-center gap-2 px-5 py-3 border rounded-sm transition-all font-body font-medium ${
                  reel.is_shared
                    ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                    : "border-zinc-800 hover:border-[#FFB800] hover:text-[#FFB800] text-zinc-400"
                }`}
              >
                <Share2 className="w-4 h-4" />
                {reel.is_shared ? "Condiviso" : "Condividi white-label"}
                {!isAgency && <Crown className="w-3.5 h-3.5 text-[#FFB800]" />}
              </button>
              <button
                onClick={onDelete}
                data-testid="detail-delete"
                className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-800 hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-400 text-zinc-400 rounded-sm transition-all"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>
      </div>

      {trimFile && (
        <VideoTrimModal
          file={trimFile}
          confirming={trimUploading}
          onCancel={onTrimCancel}
          onConfirm={onTrimConfirm}
        />
      )}

      {socialOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeSocialModal}
          data-testid="social-share-modal"
        >
          <div
            className="w-full max-w-lg border border-zinc-800 bg-zinc-950 rounded-sm p-6 md:p-8 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-4 h-4 text-[#FF4400]" />
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400]">
                    · Pubblica il tuo reel
                  </div>
                </div>
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter text-white">
                  {socialMode === "ready"
                    ? "MP4 pronto!"
                    : socialMode === "native"
                      ? "Condividi sui social"
                      : "Trasferisci sul telefono"}
                </h2>
              </div>
              <button
                onClick={closeSocialModal}
                className="w-8 h-8 rounded-sm flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900"
                data-testid="social-share-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {socialLoading ? (
              <div className="py-12 flex flex-col items-center text-zinc-500">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF4400] mb-3" />
                <p className="font-mono text-[10px] uppercase tracking-widest">Preparazione MP4 + link sicuro...</p>
              </div>
            ) : (
              <div>
                {socialMode === "ready" && (
                  <div>
                    <p className="text-zinc-400 font-body mb-5">
                      Il tuo reel è pronto. Tocca il pulsante qui sotto per aprire il menu di condivisione del telefono e scegli Instagram, TikTok, WhatsApp o qualsiasi altra app.
                    </p>
                    <button
                      onClick={onShareNow}
                      data-testid="social-share-now-btn"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-black text-lg tracking-tight rounded-sm transition-colors"
                    >
                      <Share2 className="w-5 h-5" />
                      Tocca per condividere
                    </button>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-3 text-center">
                      Apre Instagram · TikTok · WhatsApp · Telegram · Email...
                    </p>
                  </div>
                )}

                {socialMode === "qr" && (
                  <div>
                    <p className="text-zinc-400 font-body mb-5">
                      Instagram, TikTok e gli altri social caricano i video <span className="text-white font-bold">solo dal telefono</span>. Inquadra il QR per scaricare l'MP4 e poi caricalo dall'app del social.
                    </p>
                    <div className="flex flex-col items-center gap-4 p-6 border border-zinc-800 bg-white rounded-sm">
                      {socialQrUrl ? (
                        <QRCodeSVG
                          value={socialQrUrl}
                          size={220}
                          level="M"
                          marginSize={2}
                          data-testid="social-share-qr"
                        />
                      ) : (
                        <div className="w-[220px] h-[220px] bg-zinc-200 rounded-sm" />
                      )}
                      <div className="flex items-center gap-2 text-zinc-700">
                        <Smartphone className="w-4 h-4" />
                        <span className="font-mono text-[10px] uppercase tracking-widest">Scan dal telefono</span>
                      </div>
                    </div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-3 text-center">
                      Link valido 48 ore · Sicuro · Solo per te
                    </p>

                    <ol className="mt-6 space-y-2 text-sm text-zinc-300 font-body">
                      <li className="flex gap-3">
                        <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">1</span>
                        <span>Inquadra il QR con la fotocamera del telefono</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">2</span>
                        <span>Scarica l'MP4 nella galleria</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-display font-black text-[#FF4400] w-5 flex-shrink-0">3</span>
                        <span>Apri Instagram/TikTok, carica il video e incolla la caption</span>
                      </li>
                    </ol>
                  </div>
                )}

                {socialMode === "native" && (
                  <p className="text-zinc-400 font-body mb-5">
                    Apertura del menu di condivisione del sistema...
                  </p>
                )}

                <div className="mt-6 border-t border-zinc-800 pt-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Caption pronta da incollare</div>
                    <button
                      onClick={onCopySocialCaption}
                      data-testid="social-share-copy-caption"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-[#FF4400] rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      Copia tutto
                    </button>
                  </div>
                  <pre
                    className="whitespace-pre-wrap text-xs text-zinc-300 font-body bg-zinc-900 border border-zinc-800 rounded-sm p-3 max-h-40 overflow-y-auto"
                    data-testid="social-share-caption-preview"
                  >
                    {buildSocialCaption(reel)}
                  </pre>

                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    {canNativeShareFile() && socialMode === "qr" && (
                      <button
                        onClick={onOpenSocialShare}
                        data-testid="social-share-retry-native"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm text-sm transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        Riapri menu condivisione
                      </button>
                    )}
                    {socialQrUrl && (
                      <a
                        href={socialQrUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="social-share-open-link"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-zinc-800 hover:border-[#FF4400] text-zinc-300 hover:text-[#FF4400] rounded-sm text-sm transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Apri link MP4
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="w-full max-w-lg border border-zinc-800 bg-zinc-950 rounded-sm p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-[#FFB800]" />
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFB800]">
                    · White-label Share
                  </div>
                </div>
                <h2 className="font-display font-black text-2xl tracking-tighter text-white">
                  {isAgency ? "Condividi col tuo branding" : "Sblocca con Agency"}
                </h2>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 rounded-sm flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {!isAgency && (
              <div>
                <p className="text-zinc-400 font-body mb-6">
                  La condivisione pubblica col <span className="text-white font-bold">tuo brand</span> (logo e colori della tua agenzia, senza menzione Reelio) è inclusa nel piano Agency.
                </p>
                <div className="border border-zinc-800 bg-black rounded-sm p-4 mb-6 text-sm text-zinc-300 font-body">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB800] mb-2">Cosa ottieni</div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#FFB800] mt-0.5 flex-shrink-0" />
                      URL custom <code className="font-mono text-xs text-white">socialreel.app/w/tuoslug/reel-id</code>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#FFB800] mt-0.5 flex-shrink-0" />
                      Logo e colori della tua agenzia nell'header
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#FFB800] mt-0.5 flex-shrink-0" />
                      Nessun branding Reelio visibile al cliente
                    </li>
                  </ul>
                </div>
                <Link
                  to="/pricing"
                  data-testid="share-upgrade"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FFB800] hover:bg-[#FFC933] text-black font-display font-bold rounded-sm transition-colors"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade a Agency €49/mese
                </Link>
              </div>
            )}

            {isAgency && shareInfo && (
              <div>
                <p className="text-zinc-400 font-body mb-6">
                  Il tuo reel è ora pubblico al seguente URL. Chi lo apre vedrà <span className="text-white font-bold">solo il tuo brand</span>.
                </p>
                <div className="border border-zinc-800 bg-black rounded-sm p-3 mb-6">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF4400] mb-2">URL pubblico</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[#FF4400] font-mono text-xs truncate" data-testid="share-url">{shareInfo.url}</code>
                    <button
                      onClick={() => { copyText(shareInfo.url, "URL"); }}
                      data-testid="share-copy"
                      className="px-2 py-1.5 border border-zinc-800 hover:border-[#FF4400] text-zinc-400 hover:text-[#FF4400] rounded-sm"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex gap-3">
                  <a
                    href={shareInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-open"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    Apri anteprima
                  </a>
                  <button
                    onClick={onUnshare}
                    data-testid="share-unshare"
                    className="px-5 py-3 border border-zinc-800 hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-400 text-zinc-400 font-body rounded-sm transition-all"
                  >
                    Disattiva
                  </button>
                </div>
              </div>
            )}

            {isAgency && !shareInfo && !sharing && (
              <p className="text-zinc-400 font-body">Preparazione del link in corso...</p>
            )}
          </div>
        </div>
      )}

      {editingScene && reel && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !savingScene && setEditingScene(null)}
          data-testid="scene-edit-modal"
        >
          <div
            className="w-full max-w-4xl border border-zinc-800 bg-zinc-950 rounded-sm p-6 md:p-8 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-2">
                  · Modifica Scena {editingScene.scene_number}
                </div>
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter text-white">
                  Aggiusta testi e posizione
                </h2>
              </div>
              <button
                onClick={() => !savingScene && setEditingScene(null)}
                className="w-8 h-8 rounded-sm flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
              {/* Live preview. On scene 1 the rendered MP4 swaps in
                  reel.title / reel.hook; we mirror that here so the
                  preview is faithful. */}
              <div className="md:col-span-2">
                <ScenePreview
                  scene={{
                    ...reel.scenes[editingScene.scene_number - 1],
                    image_base64: editingScene.new_image_base64 || reel.scenes[editingScene.scene_number - 1].image_base64,
                  }}
                  brandColor={reel.brand_color_primary || "#FF4400"}
                  businessName={reel.business_name}
                  edit={
                    editingScene.scene_number === 1
                      ? {
                          ...editingScene,
                          headline: (editingScene.headline || "").trim() || reel.title || "",
                          subtitle: (editingScene.subtitle || "").trim() || reel.hook || "",
                        }
                      : editingScene
                  }
                  cta={reel.cta}
                />
                <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-center">
                  Anteprima in tempo reale
                </p>
              </div>

              <div className="md:col-span-3 space-y-5">
                {/* Replace media (photo or video) */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Media scena {editingScene.is_video ? "(attualmente video)" : "(attualmente foto)"}
                  </label>
                  <div className="flex items-center gap-3 mb-3 border border-zinc-800 bg-zinc-900 rounded-sm p-3">
                    <div className="w-12 h-16 rounded-sm overflow-hidden bg-zinc-950 border border-zinc-800 flex-shrink-0 relative">
                      {editingScene.is_video && !editingScene.new_image_base64 ? (
                        <div className="w-full h-full bg-gradient-to-br from-[#FF4400]/30 to-zinc-900 flex items-center justify-center">
                          <Film className="w-5 h-5 text-[#FF4400]" />
                        </div>
                      ) : (
                        <img
                          src={`data:image/png;base64,${editingScene.new_image_base64 || reel.scenes[editingScene.scene_number - 1].image_base64}`}
                          alt="current"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-white text-sm">
                        {editingScene.new_image_base64
                          ? "Nuova foto selezionata"
                          : editingScene.is_video
                          ? "Scena video"
                          : "Sostituisci media"}
                      </div>
                      <div className="text-xs text-zinc-500 font-body mt-0.5">
                        Foto o video — galleria oppure direttamente dalla camera
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <SceneFileButton
                      label="Foto · Galleria"
                      Icon={ImageIcon}
                      capture={false}
                      testId="scene-replace-photo-gallery"
                      onPicked={(b64) => setEditingScene((cur) => cur ? { ...cur, new_image_base64: b64 } : cur)}
                    />
                    <SceneFileButton
                      label="Foto · Camera"
                      Icon={Camera}
                      capture={true}
                      testId="scene-replace-photo-camera"
                      onPicked={(b64) => setEditingScene((cur) => cur ? { ...cur, new_image_base64: b64 } : cur)}
                    />
                    <SceneVideoButton
                      label="Video · Galleria"
                      Icon={Film}
                      capture={false}
                      testId="scene-replace-video-gallery"
                      disabled={savingScene}
                      onPicked={(file) => onReplaceSceneVideo(file)}
                    />
                    <SceneVideoButton
                      label="Video · Camera"
                      Icon={Camera}
                      capture={true}
                      testId="scene-replace-video-camera"
                      disabled={savingScene}
                      onPicked={(file) => onReplaceSceneVideo(file)}
                    />
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                    · Video max 30s · 100MB
                  </p>
                </div>

                {/* Regenerate with AI — only for image scenes */}
                {!editingScene.is_video && (
                <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wand2 className="w-4 h-4 text-[#FF4400]" />
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                      Modifica foto con AI
                    </label>
                  </div>

                  {/* Mode toggle: edit existing photo vs generate fresh */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setRegenMode("edit")}
                      data-testid="regen-mode-edit"
                      disabled={regenerating}
                      className={`px-3 py-2 rounded-sm text-xs font-display font-bold transition-colors text-left disabled:opacity-50 ${
                        regenMode === "edit"
                          ? "bg-[#FF4400] text-white"
                          : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-[#FF4400]"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Pencil className="w-3 h-3" />
                        Modifica questa
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-widest opacity-70 mt-0.5 normal-case">
                        Stesso soggetto · solo le tue modifiche
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegenMode("fresh")}
                      data-testid="regen-mode-fresh"
                      disabled={regenerating}
                      className={`px-3 py-2 rounded-sm text-xs font-display font-bold transition-colors text-left disabled:opacity-50 ${
                        regenMode === "fresh"
                          ? "bg-[#FF4400] text-white"
                          : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-[#FF4400]"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" />
                        Nuova da zero
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-widest opacity-70 mt-0.5 normal-case">
                        Foto completamente diversa
                      </div>
                    </button>
                  </div>

                  <p className="text-xs text-zinc-400 font-body mb-3">
                    {regenMode === "edit"
                      ? "Descrivi SOLO le modifiche che vuoi (es. 'cambia lo sguardo verso la camera'). L'AI manterrà lo stesso soggetto e composizione."
                      : "Descrivi la foto che vuoi. L'AI ne genererà una completamente nuova ignorando quella attuale."}
                  </p>
                  <textarea
                    data-testid="scene-regen-prompt"
                    rows={2}
                    value={regenPrompt}
                    onChange={(e) => setRegenPrompt(e.target.value)}
                    placeholder={regenMode === "edit"
                      ? "Es. Cambia lo sguardo verso la camera, mantieni tutto il resto identico"
                      : "Es. Mano che firma un contratto, scrivania in legno, luce calda"}
                    disabled={regenerating}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body text-sm resize-none disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={onRegenerateImage}
                    disabled={regenerating || savingScene || (regenMode === "edit" && !regenPrompt.trim())}
                    data-testid="scene-regen-btn"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors text-sm"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : (regenMode === "edit" ? <Pencil className="w-4 h-4" /> : <Wand2 className="w-4 h-4" />)}
                    {regenerating ? "Elaborazione..." : (regenMode === "edit" ? "Applica modifica" : "Genera foto nuova")}
                  </button>
                  {regenMode === "edit" && !regenPrompt.trim() && (
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
                      · Scrivi cosa vuoi modificare
                    </p>
                  )}
                  {regenerating && (
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
                      Può richiedere 15-30 secondi
                    </p>
                  )}
                </div>
                )}

                {/* Scene duration slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <Clock className="w-3.5 h-3.5" />
                      Durata scena
                    </label>
                    <span className="font-mono text-xs text-[#FF4400] font-bold" data-testid="scene-duration-label">
                      {(editingScene.duration_ms / 1000).toFixed(1)}s
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max={editingScene.is_video ? 30000 : 6000}
                    step="100"
                    data-testid="scene-duration-slider"
                    value={editingScene.duration_ms}
                    onChange={(e) => setEditingScene({ ...editingScene, duration_ms: parseInt(e.target.value, 10) })}
                    className="w-full h-1 bg-zinc-800 rounded-sm appearance-none cursor-pointer accent-[#FF4400]"
                  />
                  <div className="flex justify-between text-[10px] font-mono text-zinc-600 mt-1">
                    <span>1.0s veloce</span>
                    <span>5.0s default</span>
                    <span>{editingScene.is_video ? "30.0s max video" : "6.0s lento"}</span>
                  </div>
                  {editingScene.is_video && (
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF4400] mt-2">
                      · Scena video — durata max 30s
                    </p>
                  )}
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Headline</label>
                  <input
                    type="text"
                    data-testid="scene-edit-headline"
                    value={editingScene.headline}
                    onChange={(e) => setEditingScene({ ...editingScene, headline: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Sottotitolo</label>
                  <input
                    type="text"
                    data-testid="scene-edit-subtitle"
                    value={editingScene.subtitle}
                    onChange={(e) => setEditingScene({ ...editingScene, subtitle: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Posizione testo</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: "auto", label: "Auto", Icon: Sparkles },
                      { id: "top", label: "Alto", Icon: ArrowUp },
                      { id: "center", label: "Centro", Icon: AlignCenterVertical },
                      { id: "bottom", label: "Basso", Icon: ArrowDown },
                    ].map(({ id, label, Icon }) => (
                      <button
                        key={id}
                        type="button"
                        data-testid={`scene-pos-${id}`}
                        onClick={() => setEditingScene({ ...editingScene, text_position: id })}
                        className={`flex flex-col items-center gap-1 px-3 py-3 rounded-sm border transition-all ${
                          (editingScene.text_position || "center") === id
                            ? "bg-[#FF4400] border-[#FF4400] text-white"
                            : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-display font-bold text-xs">{label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-2">
                    La call-to-action è posizionata in basso, sopra la zona caption di IG/TikTok. Quando scegli "Basso", il testo viene tenuto subito sopra la CTA.
                  </p>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Dimensione testo</label>
                  <div className="flex gap-2">
                    {[
                      { id: "small", label: "Piccolo" },
                      { id: "normal", label: "Normale" },
                      { id: "large", label: "Grande" },
                    ].map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`scene-size-${s.id}`}
                        onClick={() => setEditingScene({ ...editingScene, text_size: s.id })}
                        className={`flex-1 px-3 py-2 rounded-sm border transition-all font-body ${
                          editingScene.text_size === s.id
                            ? "bg-[#FF4400] border-[#FF4400] text-white font-bold"
                            : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
                  <button
                    onClick={() => setEditingScene(null)}
                    disabled={savingScene}
                    className="px-4 py-3 text-zinc-400 hover:text-white rounded-sm transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={onSaveScene}
                    disabled={savingScene}
                    data-testid="scene-edit-save"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-50 text-white font-display font-bold rounded-sm transition-colors"
                  >
                    {savingScene ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Salva scena
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cross-create modal: ask "make also a carousel from this idea?" */}
      <CrossCreatePrompt
        source={reel}
        targetType="carousel"
        mode="modal"
        open={crossOpen}
        onClose={() => setCrossOpen(false)}
      />

      {/* Voice recording teleprompter: full-screen modal that displays
          the narration script big and offers Record/Pause/Stop controls.
          On confirm, the audio is uploaded to /narration/upload-audio
          and replaces any existing TTS take. */}
      <NarrationRecorder
        open={narrationRecorderOpen}
        reelId={id}
        mode={narrationMode}
        onClose={() => setNarrationRecorderOpen(false)}
        onUploaded={async (data) => {
          // Re-fetch the full reel so the (auto-synced) scene durations
          // are reflected in the player + scene grid + downstream MP4
          // render. A partial state merge would leave stale duration_ms
          // on the scenes after the backend redistributed them.
          try {
            const { data: fresh } = await api.get(`/reels/${id}`);
            setReel(fresh);
          } catch {
            setReel((r) => r ? {
              ...r,
              narration_voice: data?.voice || "user_recording",
              narration_has_audio: true,
              narration_mode: data?.mode || r.narration_mode || "overlay",
            } : r);
          }
          setNarrationAudioVersion((v) => v + 1);
        }}
      />
    </div>
  );
};

const Section = ({ label, children, onCopy }) => (
  <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
    <div className="flex items-center justify-between mb-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      {onCopy && (
        <button
          onClick={onCopy}
          className="text-zinc-500 hover:text-[#FF4400] transition-colors p-1"
          title="Copia"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    {children}
  </div>
);

// Compress a File to JPEG base64 (no data: prefix), max 1080x1920, quality auto-downscaled
const compressImageFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX_W = 1080, MAX_H = 1920;
      let { width, height } = img;
      const ratio = Math.min(MAX_W / width, MAX_H / height, 1);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const tryAt = (q) => {
        const dataUrl = canvas.toDataURL("image/jpeg", q);
        const b64 = dataUrl.split(",")[1] || "";
        const approxBytes = b64.length * 0.75;
        if (approxBytes <= 1.4 * 1024 * 1024 || q <= 0.4) return b64;
        return tryAt(q - 0.1);
      };
      try { resolve(tryAt(0.82)); } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("Immagine non leggibile"));
    img.src = String(reader.result);
  };
  reader.onerror = () => reject(new Error("Errore lettura file"));
  reader.readAsDataURL(file);
});

const SceneFileButton = ({ label, Icon, capture, testId, onPicked }) => (
  <label
    className="flex flex-col items-center justify-center gap-1 cursor-pointer border-2 border-dashed border-zinc-800 hover:border-[#FF4400] bg-zinc-950 hover:text-[#FF4400] text-zinc-400 rounded-sm py-4 transition-colors"
    data-testid={testId}
  >
    <Icon className="w-5 h-5" />
    <span className="font-display font-bold text-xs text-center px-1">{label}</span>
    <input
      type="file"
      accept="image/*"
      {...(capture ? { capture: "environment" } : {})}
      className="hidden"
      onChange={async (e) => {
        const input = e.target;
        const f = input.files?.[0];
        if (!f) return;
        try {
          const b64 = await compressImageFile(f);
          onPicked(b64);
          toast.info("Foto pronta — click su 'Salva scena' per applicare");
        } catch (err) {
          toast.error(err.message || "Errore lettura file");
        }
        try { if (input && input.isConnected) input.value = ""; } catch (_) {}
      }}
    />
  </label>
);

// Pick a video from gallery or camera. Uploads immediately to /reels/uploads/video
// via the parent's onPicked handler (which handles the multipart POST + reel patch).
const SceneVideoButton = ({ label, Icon, capture, testId, onPicked, disabled }) => (
  <label
    className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-sm py-4 transition-colors ${
      disabled
        ? "border-zinc-900 bg-zinc-950 text-zinc-600 cursor-not-allowed"
        : "border-zinc-800 hover:border-[#FF4400] bg-zinc-950 hover:text-[#FF4400] text-zinc-400 cursor-pointer"
    }`}
    data-testid={testId}
  >
    <Icon className="w-5 h-5" />
    <span className="font-display font-bold text-xs text-center px-1">{label}</span>
    <input
      type="file"
      accept="video/*"
      {...(capture ? { capture: "environment" } : {})}
      disabled={disabled}
      className="hidden"
      onChange={async (e) => {
        const input = e.target;
        const f = input.files?.[0];
        if (f) {
          try {
            await onPicked(f);
          } catch (err) {
            toast.error(err.message || "Errore lettura video");
          }
        }
        try { if (input && input.isConnected) input.value = ""; } catch (_) {}
      }}
    />
  </label>
);

// 9:16 dashed card to add a scene; offers 4 quick-pick buttons (photo / video, gallery / camera).
const AddSceneMediaCard = ({ onPhoto, onVideo }) => (
  <div
    className="aspect-[9/16] border-2 border-dashed border-zinc-800 hover:border-[#FF4400]/60 bg-zinc-950 rounded-sm flex flex-col p-3 transition-colors"
    data-testid="scene-add"
  >
    <div className="flex flex-col items-center justify-center text-center mb-3 mt-2">
      <Sparkles className="w-5 h-5 text-[#FF4400] mb-1.5" />
      <span className="font-display font-bold text-sm text-white">Aggiungi scena</span>
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">foto o video</span>
    </div>
    <div className="grid grid-cols-2 gap-1.5 mt-auto">
      <AddSceneSlot label="Foto" sub="Galleria" Icon={ImageIcon} accept="image/*" capture={false} testId="scene-add-photo-gallery" onPicked={onPhoto} />
      <AddSceneSlot label="Foto" sub="Camera" Icon={Camera} accept="image/*" capture={true} testId="scene-add-photo-camera" onPicked={onPhoto} />
      <AddSceneSlot label="Video" sub="Galleria" Icon={Film} accept="video/*" capture={false} testId="scene-add-video-gallery" onPicked={onVideo} />
      <AddSceneSlot label="Video" sub="Camera" Icon={Camera} accept="video/*" capture={true} testId="scene-add-video-camera" onPicked={onVideo} />
    </div>
  </div>
);

const AddSceneSlot = ({ label, sub, Icon, accept, capture, testId, onPicked }) => (
  <label
    className="flex flex-col items-center justify-center gap-0.5 cursor-pointer border border-zinc-800 hover:border-[#FF4400] hover:bg-[#FF4400]/5 hover:text-[#FF4400] text-zinc-400 rounded-sm py-2.5 transition-colors"
    data-testid={testId}
  >
    <Icon className="w-3.5 h-3.5" />
    <span className="font-display font-bold text-[11px] leading-none">{label}</span>
    <span className="font-mono text-[8px] uppercase tracking-widest leading-none">{sub}</span>
    <input
      type="file"
      accept={accept}
      {...(capture ? { capture: "environment" } : {})}
      className="hidden"
      onChange={async (e) => {
        const input = e.target;
        const f = input.files?.[0];
        if (f) {
          try {
            await onPicked(f);
          } catch (err) {
            toast.error(err.message || "Errore lettura file");
          }
        }
        try { if (input && input.isConnected) input.value = ""; } catch (_) {}
      }}
    />
  </label>
);

// Live preview used inside the scene edit modal
const ScenePreview = ({ scene, brandColor, businessName, edit, cta }) => {
  if (!scene) return null;
  const pos = edit.text_position;
  const size = edit.text_size;
  const headlinePx = size === "small" ? 22 : size === "large" ? 46 : 32;
  const subtitlePx = size === "small" ? 10 : size === "large" ? 14 : 11;
  // Position the text container via top/bottom (both are % of the parent
  // HEIGHT, unlike padding-% which is relative to the parent WIDTH).
  //
  // CTA pill sits at `bottom: 18%` → its top edge is around 22-24% from
  // the bottom. We give the headline a 4% breathing zone above it.
  const containerStyle =
    pos === "top"
      ? { top: "8%", left: 0, right: 0 }
      : pos === "bottom"
      ? { bottom: "26%", left: 0, right: 0 }
      : { top: "20%", bottom: "32%", left: 0, right: 0, justifyContent: "center" };
  return (
    <div
      className="relative w-full rounded-sm overflow-hidden border border-zinc-800 bg-black"
      style={{ aspectRatio: "9/16" }}
    >
      <img
        src={`data:image/png;base64,${scene.image_base64}`}
        alt="Scene preview"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
      {/* Badge top-left */}
      <div
        className="absolute top-3 left-3 px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold text-white"
        style={{ backgroundColor: brandColor }}
      >
        {businessName}
      </div>
      {/* Headline + subtitle. Uses top/bottom positioning (NOT padding-%)
          to anchor reliably regardless of aspect ratio. */}
      <div
        className="absolute flex flex-col items-center px-4"
        style={containerStyle}
      >
        <div
          className="font-display font-black text-white text-center leading-[0.95] drop-shadow-[0_4px_8px_rgba(0,0,0,0.85)]"
          style={{ fontSize: `${headlinePx}px`, letterSpacing: "-0.02em" }}
        >
          {edit.headline.toUpperCase()}
        </div>
        {edit.subtitle && (
          <div
            className="text-white/90 font-body text-center mt-1 px-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
            style={{ fontSize: `${subtitlePx}px` }}
          >
            {edit.subtitle}
          </div>
        )}
      </div>
      {/* BOTTOM CTA pill — anchored at 18% from bottom (well above the
          IG/TikTok caption safe-zone) and visually mirroring the final
          MP4 render position. */}
      {cta && (
        <div
          className="absolute left-3 right-3 px-2 py-1.5 rounded-sm text-white text-[10px] font-display font-black text-center tracking-wide uppercase truncate"
          style={{ backgroundColor: brandColor, bottom: "18%" }}
        >
          {cta}
        </div>
      )}
      {/* Faux IG/TikTok caption safe-zone marker — the bottom 14% of the
          frame is covered by the platform UI in production. */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: "14%" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-2 left-3 right-3 text-[9px] font-mono uppercase tracking-widest text-white/40">
          ↑ qui sotto IG/TikTok mette la caption
        </div>
      </div>
    </div>
  );
};

export default ReelDetail;
